import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  additionalExpenses,
  type AdditionalExpenseListItem,
} from '@/api/additionalExpenses';
import { contractors as contractorsApi } from '@/api/catalogs';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABEL: Record<string, string> = {
  TRANSPORT: 'Транспорт',
  INSURANCE: 'Страхування',
  CUSTOMS_BROKER: 'Митний брокер',
  OTHER: 'Інше',
};

export default function AdditionalExpensesPage() {
  const orgNames = useOrgNames();
  const navigate = useNavigate();
  // Стандартні фільтри списків (2026-07-29, ревізія Покупок).
  const f = useDocListFilters();
  // 2026-08-09 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<AdditionalExpenseListItem>(
    (limit, offset, sort) => additionalExpenses.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { attachmentsType: 'doc.additional_expenses' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<AdditionalExpenseListItem>('doc.additional_expenses', f.orgId);
  const ctrQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const ctrName = useMemo(() => {
    const m = new Map<string, string>();
    ctrQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [ctrQ.data]);

  const cols = useMemo<ColDef<AdditionalExpenseListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: AdditionalExpenseListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 130, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'contractor_id', headerName: 'Постачальник послуги', flex: 1, minWidth: 200, sortable: true,
      valueFormatter: (p) => ctrName.get(p.value) ?? '',
    },
    {
      field: 'expense_type', headerName: 'Вид', width: 160, sortable: false,
      valueFormatter: (p) => p.value ? (TYPE_LABEL[p.value] ?? p.value) : '',
    },
    {
      field: 'amount_without_vat', headerName: 'Без ПДВ', width: 130, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'vat_amount', headerName: 'ПДВ', width: 110, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'total_amount', headerName: 'Усього', width: 130, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [ctrName]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Додаткові витрати на придбання<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/additional-expenses/new')}>
          + Новий документ
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <InfiniteDocGrid<AdditionalExpenseListItem>
        copyRow={(r) => navigate(`/additional-expenses/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => additionalExpenses.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="additional-expenses"
        onRowDoubleClicked={(r) => navigate(`/additional-expenses/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
